import React, {FormEvent, useMemo, useState} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Formik, Form, Field } from 'formik';
import {Button, Divider, Grid, Group, Text, TextInput} from '@mantine/core';
import OrderItemInput from '../../../../../components/util/form-components/OrderItemInput/OrderItemInput';
import { CommonState } from '../../../../../redux';
import { itemStore } from '../../../../../redux/entities/inventory/item';
import { categoryStore } from '../../../../../redux/entities/inventory/category';
// import useStandardPrint from "../../../../../hooks/useStandardPrint/useStandardPrint";
import {AxiosError} from "axios";
import {getErrorResponseMessage} from "../../../../../util/http";
import {EOrderStatus, Order, orderStore} from "../../../../../redux/entities/inventory/order";
import {makeDeposit} from "../../../../../redux/factory/inventory/order";
import StandardModal from '../../../../../components/util/StandardModal/StandardModal';
import { AppTheme } from '../../../../../appTheme';
import Select from 'react-select';
import AdministratorComponent from "../../../../../components/util/RoleGatedComponent/AdministratorComponent";
import {RoleProtectedComponent} from "../../../../../components/util/RoleProtectedRoute/RoleProtectedComponent";
import {auditLogStore} from "../../../../../redux/entities/auditlog";

const orderStatusDropdownOptions = [
  {value: EOrderStatus.Unapproved, label: 'Unapproved'},
  {value: EOrderStatus.Approved, label: 'Approved'},
  {value: EOrderStatus.Cancelled, label: 'Cancelled'}
];

const getOrderStatus = (val: EOrderStatus) => orderStatusDropdownOptions.find(o => o.value === val) ?? orderStatusDropdownOptions[0];


type Props = {
  deposit: Order;
  show: boolean;
  onCancel: () => void;
  onSubmit: (values?: Order) => void;
  readOnlyInitial?: boolean;
} & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const DepositModal: React.FC<Props> = ({ show, onCancel, onSubmit, items, categories, deposit, readOnlyInitial, actions: {upsertDeposit, loadAuditLogs} }) => {

  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentOrderStatus, setCurrentOrderStatus] = useState(getOrderStatus(deposit.orderStatus));
  // const printRef = useRef(null);
  // const handlePrint = useStandardPrint(printRef);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(!!readOnlyInitial);

  const processSubmit = async (e: FormEvent<HTMLFormElement>, association: Order) => {
    setIsSubmitting(true);
    setErrorMessage('');
    e.persist();
    e.preventDefault();
    try {
      // Filter out empty order items similar to public landing behavior
      // For deposits, consider warehouseQuantity as the primary quantity field
      const filteredItems = (association.orderItems ?? []).filter(oi =>
        (oi?.storeQuantity !== undefined && oi?.storeQuantity > 0) ||
        (oi?.warehouseQuantity !== undefined && oi?.warehouseQuantity > 0) ||
        (oi?.itemOptions?.length > 0 && oi?.itemOptions?.find(o => o.quantity > 0))
      );
      const request: Order = {
        ...association,
        orderItems: filteredItems,
        orderStatus: currentOrderStatus.value,
        id: deposit.id
      };
      await upsertDeposit(request);
      await onSubmit();
    } catch (e: AxiosError | any) {
      setErrorMessage(getErrorResponseMessage(e));
    }
    setIsSubmitting(false);
  };

  // Memoized map for quick item lookup by id
  const itemById = useMemo(() => {
    const map: Record<string, { name: string }> = {};
    (items || []).forEach(it => { map[it.id] = { name: it.name }; });
    return map;
  }, [items]);

  const modalTitle = (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <h1 style={{color: AppTheme.colors.themeGreen}}>Deposit Order</h1>
      {/* Push controls to the far right so they can hug the modal edge */}
      <Group style={{ display: 'flex', marginLeft: 'auto', marginRight: '2rem', fontWeight: 'initial', fontSize: '1.2rem' }}>
        {deposit?.id &&
          <>
            <Select
            styles={{
              control: (base) => ({ ...base, border: '1px solid', borderColor: AppTheme.colors.themePurple, opacity: isReadOnly ? 0.6 : 1 })
            }}
            isDisabled={isReadOnly}
            menuPlacement={'auto'}
            value={currentOrderStatus}
            options={orderStatusDropdownOptions}
            isClearable={false}
            isOptionSelected={(val) => val === currentOrderStatus}
            isSearchable={false}
            onChange={(newValue) => setCurrentOrderStatus(newValue ?? getOrderStatus(deposit.orderStatus))}
          />
            <Button style={{ marginLeft: '12px', backgroundColor: AppTheme.colors.themePurple }} onClick={() => setIsReadOnly(!isReadOnly)}>
              {isReadOnly ? 'Edit' : 'View'}
            </Button>
        </>
        }
      </Group>
    </div>
  );

  return (
    <StandardModal
      opened={show}
      onCancel={onCancel}
      title={modalTitle}
      size={'70vw'}
      submitText={isReadOnly ? 'Close' : 'Submit'}
      cancelText={isReadOnly ? 'Close' : 'Cancel'}
      submitDisabled={isSubmitting || !!isReadOnly}
      loading={isSubmitting}
      showSubmit={!isReadOnly}
      formId={isReadOnly ? undefined : 'deposit-form'}
      onSubmit={isReadOnly ? onCancel : undefined}
      errorMessage={errorMessage}
    >
      <Formik<Order>
        initialValues={makeDeposit(deposit)}
        onSubmit={(vals, { setSubmitting }) => {
          const fakeEvent = { preventDefault: () => {}, persist: () => {} } as any as FormEvent<HTMLFormElement>;
          // Reuse existing submit pipeline for consistency
          // Note: processSubmit expects a real event; we shim it here
          processSubmit(fakeEvent, vals);
          setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => (
          <Form onSubmit={handleSubmit} id={'deposit-form'}>
            <Grid gutter="lg" style={{ marginTop: '1rem' }}>
              {isReadOnly ? (
                <>
                  <Grid.Col span={12}>
                    <Text fw={600} size="sm">Organization</Text>
                    <Text>{values.organization || '-'}</Text>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Divider my={4} variant="dashed" />
                    <Text fw={700} size="sm" style={{ color: AppTheme.colors.themeGreen, marginBottom: 8 }}>Items to Deposit</Text>
                    {(() => {
                      const itemsList = (values.orderItems ?? []).filter(oi => (oi.storeQuantity ?? 0) > 0 || (oi.warehouseQuantity ?? 0) > 0);
                      if (itemsList.length === 0) return <Text color="dimmed">No items selected.</Text>;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {itemsList.map((oi, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', border: '1px solid #eee', borderRadius: 6, padding: '8px 12px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontWeight: 600 }}>{itemById[oi.itemId]?.name ?? oi.itemId}</span>
                              </div>
                              <div style={{ display: 'flex', gap: 12, fontSize: 14 }}>
                                {(oi.storeQuantity ?? 0) > 0 && <span>Store: {oi.storeQuantity}</span>}
                                {(oi.warehouseQuantity ?? 0) > 0 && <span>Warehouse: {oi.warehouseQuantity}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </Grid.Col>
                </>
              ) : (
                <>
                  <Grid.Col span={12}>
                    <Field name={'organization'}>
                      {({ field }: any) => (
                        <TextInput
                          label="Organization"
                          placeholder="Enter organization name"
                          value={field.value}
                          onChange={(e) => setFieldValue(field.name, e.currentTarget.value)}
                        />
                      )}
                    </Field>
                  </Grid.Col>

                  <Grid.Col span={12}>
                    <OrderItemInput
                      items={items}
                      categories={categories}
                      showStoreQuantity={true}
                      showWarehouseQuantity={true}
                      thinnerCategories={true}
                    />
                  </Grid.Col>
                </>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </StandardModal>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({ actions: bindActionCreators({
    upsertDeposit: orderStore.actions.upsertDeposit,
    loadAuditLogs: auditLogStore.actions.setIncludedData
  }, dispatch) });
const mapStateToProps = (state: CommonState) => ({
  items: itemStore.selectors.unarchivedItems(state),
  categories: categoryStore.selectors.getAsArray(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(DepositModal);
